// Rechtes Details-Panel des Dacheditors: Objekteinstellungen + Objektliste.
import type { ReactNode } from "react";
import type { DetailsTab, EditorObject } from "./types.js";
import { areaM2 as polyAreaM2 } from "./geo.js";
import type { RoofChecklistResult, RoofCopilotAdvice } from "@energyos/roof-core";
import { PlanningCopilot } from "../PlanningCopilot.js";
import type { RoofEdge, RoofEdgeType, ExclusionKind } from "@energyos/shared-types";
import {
  EDGE_TYPE_OPTIONS,
  KEEPOUT_REASON_OPTIONS,
  keepoutReasonLabel,
} from "./semantic-edit.js";

interface DetailsPanelProps {
  detailsTab: DetailsTab;
  onTabChange: (t: DetailsTab) => void;

  bgEdit: boolean;
  rasterOpacity: number; // 0.2–1
  onRasterOpacity: (v: number) => void;
  mpp: number; // Maßstab [m/px] (read-only)
  bgExtra?: ReactNode; // zusätzliche Hintergrund-Steuerung (z. B. Drohnenbild)

  objects: EditorObject[]; // bereits nach Tab gefiltert
  selected: EditorObject | null;
  checklist?: RoofChecklistResult | null; // Prüfampel (Stage 1), additiv
  copilotAdvice?: RoofCopilotAdvice | null; // KI-Copilot (Stage KI-1), additiv
  // Stage 2: semantische Objekte der ausgewählten Fläche (Kanten/Sperrflächen)
  onAddEdge?: (type: RoofEdgeType) => void;
  onUpdateEdge?: (edgeId: string, patch: Partial<Omit<RoofEdge, "id">>) => void;
  onRemoveEdge?: (edgeId: string) => void;
  onAddKeepout?: (kind: ExclusionKind) => void;
  onRemoveKeepout?: (keepoutId: string) => void;
  roofCount: number;
  obstacleCount: number;
  totalRoofAreaM2: number;

  allScalePct: number;
  allRotDeg: number;

  onSelect: (id: string | null) => void;
  onSetName: (name: string) => void;
  onSetTilt: (deg: number) => void;
  onSetNorth: (deg: number) => void;
  onSetHeightZ: (m: number) => void;
  onSetPosition: (axis: "x" | "y", meters: number) => void;
  onScale: (pct: number) => void;
  onRotate: (deg: number) => void;
  onToggleProjektion: (value: boolean) => void;
  onToggleHidden: (id: string) => void;
  onToggleLocked: (id: string) => void;
  onDelete: () => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
      <span style={{ fontSize: 11, color: "#64748b", width: 96, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "4px 7px",
  border: "1px solid #cbd5e1",
  borderRadius: 5,
  fontSize: 12,
  boxSizing: "border-box",
  background: "#fff",
};

export function RoofDetailsPanel(props: DetailsPanelProps) {
  const { selected } = props;
  const hasSel = selected !== null;
  const tiltLabel = !hasSel ? "Neigung (Alle) [°]" : selected.kind === "roof" ? "Dachneigung [°]" : "Neigung [°]";
  const scaleValue = hasSel ? selected.scalePct : props.allScalePct;
  const rotValue = hasSel ? selected.rotDeg : props.allRotDeg;

  return (
    <div
      style={{
        width: 300,
        flexShrink: 0,
        background: "#f4f6f9",
        borderLeft: "1px solid #d4dde6",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "9px 14px",
          background: "#1f59bd",
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        » Details
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #d4dde6", background: "#fff", flexShrink: 0 }}>
        {(["gebaeude", "stoer"] as const).map((t) => {
          const active = props.detailsTab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => props.onTabChange(t)}
              style={{
                flex: 1,
                padding: "8px 0",
                fontSize: 12,
                border: "none",
                cursor: "pointer",
                background: active ? "#fff" : "#eef2f6",
                color: active ? "#1f59bd" : "#64748b",
                fontWeight: active ? 700 : 500,
                borderBottom: active ? "2px solid #1f59bd" : "2px solid transparent",
              }}
            >
              {t === "gebaeude" ? "Gebäude" : "Störflächen"}
            </button>
          );
        })}
      </div>

      {/* Hintergrund-Einstellungen */}
      {props.bgEdit && (
        <div style={{ padding: "11px 14px", borderBottom: "1px solid #d4dde6", background: "#eef4ff", flexShrink: 0 }}>
          <p style={{ fontSize: 11, fontWeight: 700, margin: "0 0 8px", color: "#1e40af" }}>
            Hintergrund-Einstellungen
          </p>
          <label style={{ fontSize: 11, color: "#374151", display: "block", marginBottom: 8 }}>
            Transparenz: {Math.round(props.rasterOpacity * 100)} %
            <input
              type="range"
              min={20}
              max={100}
              value={Math.round(props.rasterOpacity * 100)}
              onChange={(e) => props.onRasterOpacity(Number(e.target.value) / 100)}
              style={{ width: "100%", marginTop: 3, accentColor: "#2563eb" }}
            />
          </label>
          <p style={{ fontSize: 10, color: "#64748b", margin: "6px 0 0" }}>
            Maßstab: {props.mpp.toFixed(3)} m/px
          </p>
          {props.bgExtra}
        </div>
      )}

      {/* KI-Copilot (Stage KI-1) — interpretiert die Prüfampel, gibt nichts frei */}
      {props.copilotAdvice && (
        <div style={{ padding: "10px 12px", borderBottom: "1px solid #d4dde6", background: "#f4f6f9", flexShrink: 0 }}>
          <PlanningCopilot advice={props.copilotAdvice} title="KI-Copilot · Dachplanung" />
        </div>
      )}

      {/* Objekteinstellungen */}
      <div style={{ padding: "11px 14px", borderBottom: "1px solid #d4dde6", background: "#fff", flexShrink: 0 }}>
        <p style={{ fontSize: 11, fontWeight: 700, margin: "0 0 9px", color: "#334155" }}>
          Objekteinstellungen ⓘ
        </p>
        <Row label="Name">
          <input
            style={inputStyle}
            value={hasSel ? selected.name : ""}
            disabled={!hasSel}
            placeholder={hasSel ? "" : "— kein Objekt gewählt —"}
            onChange={(e) => props.onSetName(e.target.value)}
          />
        </Row>
        <Row label="Position X [m]">
          <input
            type="number"
            style={inputStyle}
            disabled={!hasSel}
            placeholder="0"
            onChange={(e) => props.onSetPosition("x", Number(e.target.value))}
          />
        </Row>
        <Row label="Position Y [m]">
          <input
            type="number"
            style={inputStyle}
            disabled={!hasSel}
            placeholder="0"
            onChange={(e) => props.onSetPosition("y", Number(e.target.value))}
          />
        </Row>
        <Row label="Höhe Z [m]">
          <input
            type="number"
            style={inputStyle}
            value={hasSel ? selected.heightZ : 0}
            disabled={!hasSel}
            onChange={(e) => props.onSetHeightZ(Number(e.target.value))}
          />
        </Row>
        <Row label={tiltLabel}>
          <input
            type="number"
            min={0}
            max={90}
            style={inputStyle}
            value={hasSel ? selected.tiltDeg : 30}
            onChange={(e) => props.onSetTilt(Number(e.target.value))}
          />
        </Row>
        <Row label="Nordwinkel [°]">
          <input
            type="number"
            min={0}
            max={360}
            style={inputStyle}
            disabled={!hasSel}
            placeholder="180"
            title="Azimut: 0=Nord, 90=Ost, 180=Süd, 270=West"
            value={hasSel ? selected.northDeg : 180}
            onChange={(e) => props.onSetNorth(Number(e.target.value))}
          />
        </Row>
        <Row label="Skalieren [%]">
          <input
            type="number"
            min={1}
            style={inputStyle}
            value={Math.round(scaleValue)}
            onChange={(e) => props.onScale(Number(e.target.value))}
          />
        </Row>
        <Row label="Drehen [°]">
          <input
            type="number"
            style={inputStyle}
            value={Math.round(rotValue)}
            onChange={(e) => props.onRotate(Number(e.target.value))}
          />
        </Row>
        <label style={{ fontSize: 11, color: "#374151", display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
          <input
            type="checkbox"
            checked={hasSel ? selected.projektion : false}
            disabled={!hasSel}
            onChange={(e) => props.onToggleProjektion(e.target.checked)}
          />
          Projektion verwenden
        </label>
      </div>

      {/* Liste der Objekte */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, background: "#fff" }}>
        <p style={{ fontSize: 11, fontWeight: 700, margin: 0, padding: "10px 14px 6px", color: "#334155", flexShrink: 0 }}>
          Liste der Objekte
        </p>
        <div style={{ padding: "0 10px 4px", flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 9.5,
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "0.03em",
              padding: "0 4px 4px",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <span style={{ flex: 1 }}>Beschreibung</span>
            <span style={{ width: 46, textAlign: "right" }}>Neig.°</span>
            <span style={{ width: 42, textAlign: "right" }}>Höhe</span>
            <span style={{ width: 18 }} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 10px", display: "flex", flexDirection: "column", gap: 3 }}>
          {props.objects.length === 0 ? (
            <div style={{ padding: "16px 10px", textAlign: "center", color: "#9ca3af", fontSize: 11, lineHeight: 1.6 }}>
              Keine Objekte in dieser Ebene.
              <br />
              Werkzeug wählen und zeichnen.
            </div>
          ) : (
            props.objects.map((o) => {
              const sel = props.selected?.id === o.id;
              return (
                <div
                  key={o.id}
                  onClick={() => props.onSelect(sel ? null : o.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "5px 6px",
                    borderRadius: 6,
                    cursor: "pointer",
                    border: `1px solid ${sel ? o.color : "transparent"}`,
                    background: sel ? "#f1f5f9" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!o.hidden}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => props.onToggleHidden(o.id)}
                    title="Sichtbarkeit"
                  />
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: o.color, flexShrink: 0 }} />
                  <span
                    style={{
                      flex: 1,
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#1e293b",
                    }}
                  >
                    {o.name}
                  </span>
                  <span style={{ width: 46, textAlign: "right", fontSize: 11, color: "#64748b" }}>
                    {o.kind === "roof" ? `${o.tiltDeg}°` : "—"}
                  </span>
                  <span style={{ width: 42, textAlign: "right", fontSize: 11, color: "#64748b" }}>
                    {o.heightZ}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onToggleLocked(o.id);
                    }}
                    title={o.locked ? "Entsperren" : "Sperren"}
                    style={{
                      width: 18,
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      padding: 0,
                    }}
                  >
                    {o.locked ? "🔒" : "🔓"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Listen-Aktionen */}
        <div style={{ display: "flex", gap: 4, padding: "8px 10px", borderTop: "1px solid #e2e8f0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={props.onDelete}
            disabled={!hasSel}
            style={{
              flex: 1,
              height: 30,
              borderRadius: 6,
              border: `1px solid ${hasSel ? "#ef4444" : "#e2e8f0"}`,
              background: "#fff",
              color: hasSel ? "#ef4444" : "#cbd5e1",
              fontSize: 11,
              fontWeight: 600,
              cursor: hasSel ? "pointer" : "default",
            }}
          >
            Löschen
          </button>
          <button
            type="button"
            onClick={() => hasSel && props.onToggleLocked(selected.id)}
            disabled={!hasSel}
            style={{
              flex: 1,
              height: 30,
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: hasSel && selected.locked ? "#fef3c7" : "#fff",
              color: "#475569",
              fontSize: 11,
              fontWeight: 600,
              cursor: hasSel ? "pointer" : "default",
            }}
          >
            {hasSel && selected.locked ? "🔒 Gesperrt" : "🔓 Sperren"}
          </button>
        </div>
      </div>

      {/* Dachkanten & Sperrflächen (Stage 2) — nur für gewählte Dachfläche */}
      {selected && selected.kind === "roof" && props.onAddEdge && (
        <SemanticSection
          selected={selected}
          onAddEdge={props.onAddEdge}
          onUpdateEdge={props.onUpdateEdge}
          onRemoveEdge={props.onRemoveEdge}
          onAddKeepout={props.onAddKeepout}
          onRemoveKeepout={props.onRemoveKeepout}
        />
      )}

      {/* Prüfampel (Stage 1) — deterministische Checkliste, ENTWURF / menschlich prüfen */}
      {props.checklist && <ChecklistSection checklist={props.checklist} />}

      {/* Zusammenfassung */}
      <div style={{ padding: "9px 14px", borderTop: "1px solid #d4dde6", background: "#eafbf0", flexShrink: 0 }}>
        <span style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>
          Dachflächen {props.roofCount} · Gesamt {props.totalRoofAreaM2.toFixed(1)} m²
          {props.obstacleCount > 0 ? ` · Störflächen ${props.obstacleCount}` : ""}
        </span>
      </div>
    </div>
  );
}

const STATE_DOT: Record<string, string> = {
  ok: "#16a34a",
  info: "#94a3b8",
  warning: "#d97706",
  critical: "#dc2626",
};

const VERDICT_LABEL: Record<string, { text: string; bg: string; fg: string }> = {
  ok: { text: "plausibel", bg: "#f0fdf4", fg: "#166534" },
  advice: { text: "Hinweise", bg: "#f8fafc", fg: "#475569" },
  warning: { text: "Warnungen", bg: "#fffbeb", fg: "#92400e" },
  critical: { text: "kritisch", bg: "#fef2f2", fg: "#991b1b" },
};

/** Kanten-/Sperrflächen-Editor für die gewählte Dachfläche (Stage 2). */
function SemanticSection({
  selected,
  onAddEdge,
  onUpdateEdge,
  onRemoveEdge,
  onAddKeepout,
  onRemoveKeepout,
}: {
  selected: EditorObject;
  onAddEdge: (type: RoofEdgeType) => void;
  onUpdateEdge?: ((edgeId: string, patch: Partial<Omit<RoofEdge, "id">>) => void) | undefined;
  onRemoveEdge?: ((edgeId: string) => void) | undefined;
  onAddKeepout?: ((kind: ExclusionKind) => void) | undefined;
  onRemoveKeepout?: ((keepoutId: string) => void) | undefined;
}) {
  const { edges, keepouts } = selected.semantic;
  return (
    <div style={{ borderTop: "1px solid #d4dde6", flexShrink: 0, maxHeight: 230, overflowY: "auto", background: "#fff", padding: "8px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
        Dachkanten — {selected.name}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {EDGE_TYPE_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => onAddEdge(o.value)}
            style={{ fontSize: 10, padding: "2px 7px", border: "1px solid #cbd5e1", borderRadius: 4, background: "#f8fafc", cursor: "pointer" }}
          >
            + {o.label}
          </button>
        ))}
      </div>
      {edges.length === 0 ? (
        <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8 }}>Noch keine Kanten. „+ First"/„+ Traufe" anlegen.</div>
      ) : (
        edges.map((e) => (
          <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
            <select
              value={e.type}
              onChange={(ev) => onUpdateEdge?.(e.id, { type: ev.target.value as RoofEdgeType })}
              style={{ fontSize: 10, padding: "1px 3px", flex: "0 0 84px" }}
            >
              {EDGE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Höhe m"
              value={e.heightM ?? ""}
              onChange={(ev) => onUpdateEdge?.(e.id, { heightM: ev.target.value === "" ? null : Number(ev.target.value) })}
              style={{ fontSize: 10, width: 52, padding: "1px 3px" }}
              title="Höhe [m]"
            />
            <input
              type="text"
              placeholder="Notiz"
              value={e.note ?? ""}
              onChange={(ev) => onUpdateEdge?.(e.id, { note: ev.target.value })}
              style={{ fontSize: 10, flex: 1, minWidth: 0, padding: "1px 3px" }}
            />
            <button onClick={() => onRemoveEdge?.(e.id)} title="Löschen" style={{ fontSize: 11, border: "none", background: "none", color: "#dc2626", cursor: "pointer" }}>✕</button>
          </div>
        ))
      )}

      <div style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", margin: "10px 0 6px" }}>Sperrflächen</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {KEEPOUT_REASON_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => onAddKeepout?.(o.value)}
            style={{ fontSize: 10, padding: "2px 7px", border: "1px solid #fdba74", borderRadius: 4, background: "#fff7ed", cursor: "pointer" }}
          >
            + {o.label}
          </button>
        ))}
      </div>
      {keepouts.length === 0 ? (
        <div style={{ fontSize: 10, color: "#94a3b8" }}>Keine Sperrflächen.</div>
      ) : (
        keepouts.map((k) => (
          <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 10, color: "#92400e", flex: 1 }}>{keepoutReasonLabel(k.kind)}</span>
            <button onClick={() => onRemoveKeepout?.(k.id)} title="Löschen" style={{ fontSize: 11, border: "none", background: "none", color: "#dc2626", cursor: "pointer" }}>✕</button>
          </div>
        ))
      )}
      <div style={{ marginTop: 6, fontSize: 9, color: "#94a3b8", fontStyle: "italic" }}>
        Kante/Sperrfläche wird aus der Flächengeometrie vorbelegt; freies Zeichnen auf der Karte folgt (Stage 2b).
      </div>
    </div>
  );
}

/** Prüfampel-Sektion: Checkliste je Dachmodell. Rein darstellend (Engine = roof-core). */
function ChecklistSection({ checklist }: { checklist: RoofChecklistResult }) {
  const v = VERDICT_LABEL[checklist.verdict] ?? VERDICT_LABEL.advice!;
  return (
    <div style={{ borderTop: "1px solid #d4dde6", flexShrink: 0, maxHeight: 240, overflowY: "auto", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px 4px" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>Prüfampel</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: v.fg, background: v.bg, border: `1px solid ${v.fg}33`, borderRadius: 4, padding: "1px 6px" }}>
          {v.text}
        </span>
      </div>
      <div style={{ padding: "0 14px 8px" }}>
        {checklist.checks.map((c) => (
          <div key={c.code} title={c.message ?? ""} style={{ display: "flex", alignItems: "center", gap: 7, padding: "2px 0" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATE_DOT[c.state] ?? "#94a3b8", flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: c.state === "critical" ? "#991b1b" : "#334155", flex: 1, minWidth: 0 }}>{c.title}</span>
            {c.state === "ok" && <span style={{ fontSize: 10, color: "#16a34a" }}>✓</span>}
            {(c.state === "warning" || c.state === "critical") && <span style={{ fontSize: 10, color: STATE_DOT[c.state] }}>!</span>}
          </div>
        ))}
        <div style={{ marginTop: 6, fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>
          ENTWURF — keine automatische Freigabe. Menschliche Prüfung erforderlich.
        </div>
      </div>
    </div>
  );
}

/** Hilfs-Export: Fläche eines Editor-Objekts (für Summen). */
export function objectAreaM2(o: EditorObject): number {
  return polyAreaM2(o.polygon);
}
